// src/services/notifications/FCMService.js
//
// Camada ÚNICA de registro de token de push (Bloco J) — uma implementação,
// adapters finos por plataforma (o adapter é o próprio Capacitor + AppDelegate
// no iOS, FirebaseMessagingService no Android; aqui o token já chega FCM).
//
// Persistência: USER/{uid}/PUSH_TOKENS/{token} — UM DOC POR DEVICE. Não se
// sobrescreve token: um usuário com celular e tablet tem dois docs. O campo
// legado USER.fcm_token continua sendo gravado (último device) porque os
// senders já deployados leem só ele; sai quando backend+functions redeployarem.
//
// Permissão é decisão de PRODUTO: não se pede no primeiro segundo do
// onboarding. O pedido acontece num MOMENTO DE VALOR (treino salvo, avaliação
// registrada) — os gatilhos são configuráveis em PUSH_PROMPT.triggers e os
// pontos do app anunciam o momento via FCMService.valueMoment('treino_salvo').
//
// Regra do bloco: falha silenciosa é o inimigo. Todo caminho em que o token
// não é obtido/gravado termina em console.error + escrita consultável em
// USER.push_permission / USER.push_last_error — nunca num catch vazio.

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// ── Configuração do pedido de permissão (decisão de produto, ajustável) ──────
export const PUSH_PROMPT = {
  // Momentos de valor que liberam o pedido de permissão. Para adicionar um
  // gatilho: inclua o nome aqui e chame FCMService.valueMoment('<nome>') no
  // ponto do app em que o valor acabou de ser entregue.
  triggers: ['treino_salvo', 'avaliacao_registrada', 'dieta_gerada'],
};

// Evento pelo qual as features anunciam um momento de valor (mesmo padrão
// desacoplado do QRScanner: CustomEvent no window, sem import de React).
export const VALUE_MOMENT_EVENT = 'lesvita:push-value-moment';

// Cache local do estado de permissão — evita reperguntar e evita uma leitura
// do Firestore por boot só para saber se já pedimos.
const PERM_KEY = 'lesvita_push_permission_v1';

let _uid = null;
let _listenersAttached = false;
let _valueMomentHandler = null;
let _appInfo = null;

async function _getAppInfo() {
  if (_appInfo) return _appInfo;
  try {
    const info = await App.getInfo();
    _appInfo = { version: info?.version || '', build: info?.build || '' };
  } catch {
    _appInfo = { version: '', build: '' };
  }
  return _appInfo;
}

function _localPerm() {
  try { return JSON.parse(localStorage.getItem(PERM_KEY) || 'null'); }
  catch { return null; }
}

function _setLocalPerm(state) {
  try { localStorage.setItem(PERM_KEY, JSON.stringify(state)); } catch { /* storage cheio: estado vive no Firestore */ }
}

export const FCMService = {

  // ── Anexa a camada ao usuário logado (NÃO pede permissão) ─────────────────
  // Chamado no boot autenticado (useNotifications). Se a permissão JÁ foi
  // concedida, re-registra em silêncio — é isso que renova last_seen_at e
  // captura rotação de token. Se ainda não foi pedida, arma o gatilho de
  // momento de valor. Se foi negada, registra o estado e NÃO pergunta de novo.
  async attach(uid) {
    if (!Capacitor.isNativePlatform() || !uid) return;
    _uid = uid;

    try {
      await this._attachNativeListeners();

      const perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'granted') {
        await PushNotifications.register();
        return;
      }
      if (perm.receive === 'denied') {
        // Negada no SO. Grava uma vez (estado consultável) e para de perguntar.
        // Reativação é pela tela de ajustes → requestFromSettings().
        const local = _localPerm();
        if (!local || local.status !== 'denied') {
          await this._recordPermission('denied', 'os_check');
        }
        return;
      }
      // 'prompt': arma o pedido para o próximo momento de valor.
      this._armValueMomentTrigger();
      const local = _localPerm();
      if (!local) await this._recordPermission('not_asked', null);
    } catch (e) {
      console.error('[FCM] attach falhou:', e);
      await this._recordError('attach', e);
    }
  },

  // Alias histórico — chamadas antigas continuam funcionando.
  async initialize(uid) { return this.attach(uid); },

  // ── Momento de valor: as features chamam isto (ou disparam o CustomEvent) ─
  valueMoment(trigger) {
    try {
      window.dispatchEvent(new CustomEvent(VALUE_MOMENT_EVENT, { detail: { trigger } }));
    } catch { /* fora do browser (teste): irrelevante */ }
  },

  _armValueMomentTrigger() {
    if (_valueMomentHandler) return;
    _valueMomentHandler = async (ev) => {
      const trigger = ev?.detail?.trigger;
      if (!PUSH_PROMPT.triggers.includes(trigger)) return;
      window.removeEventListener(VALUE_MOMENT_EVENT, _valueMomentHandler);
      _valueMomentHandler = null;
      await this.requestPermission(trigger);
    };
    window.addEventListener(VALUE_MOMENT_EVENT, _valueMomentHandler);
  },

  // ── Pedido de permissão de fato ────────────────────────────────────────────
  async requestPermission(trigger) {
    if (!Capacitor.isNativePlatform() || !_uid) return 'unavailable';
    try {
      const res = await PushNotifications.requestPermissions();
      const status = res.receive === 'granted' ? 'granted' : 'denied';
      await this._recordPermission(status, trigger);
      if (status === 'granted') {
        await PushNotifications.register();
        // A permissão do SO é a mesma dos lembretes locais — avisa quem agenda
        // (useNotifications) que agora dá para agendar.
        try {
          window.dispatchEvent(new CustomEvent('lesvita:push-permission-granted'));
        } catch { /* fora do browser */ }
      }
      return status;
    } catch (e) {
      console.error('[FCM] requestPermission falhou:', e);
      await this._recordError('request_permission', e);
      return 'error';
    }
  },

  // Reativação pela tela de ajustes (gesto explícito do usuário).
  // Devolve 'blocked' quando o SO já negou em definitivo — a UI deve orientar
  // a ligar nas configurações do sistema, porque o prompt nem vai aparecer.
  async requestFromSettings() {
    if (!Capacitor.isNativePlatform() || !_uid) return 'unavailable';
    const before = await PushNotifications.checkPermissions();
    if (before.receive === 'granted') {
      await PushNotifications.register();
      await this._recordPermission('granted', 'settings');
      return 'granted';
    }
    const status = await this.requestPermission('settings');
    if (status === 'denied' && before.receive === 'denied') return 'blocked';
    return status;
  },

  async permissionState() {
    if (!Capacitor.isNativePlatform()) return 'unavailable';
    const perm = await PushNotifications.checkPermissions();
    return perm.receive; // 'granted' | 'denied' | 'prompt'
  },

  // ── Listeners nativos ──────────────────────────────────────────────────────
  async _attachNativeListeners() {
    if (_listenersAttached) return;
    // NÃO usa removeAllListeners(): derrubaria listeners de outros serviços.
    _listenersAttached = true;

    // Token FCM emitido OU rotacionado (iOS: AppDelegate posta o token FCM já
    // trocado; Android: FirebaseMessagingService entrega FCM nativamente).
    await PushNotifications.addListener('registration', async (token) => {
      await FCMService.saveToken(_uid, token.value);
    });

    // Falha de registro: erro VISÍVEL + estado consultável, nunca só warn.
    await PushNotifications.addListener('registrationError', async (err) => {
      console.error('[FCM] Erro no registro do token:', err);
      await FCMService._recordError('registration', err);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.info('[FCM] Foreground:', notification.title);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      FCMService.handleNotificationTap(data);
    });
  },

  // ── Persistência: um doc por token (multi-device) + espelho legado ────────
  async saveToken(uid, token) {
    if (!uid || !token) return;
    try {
      const platform = Capacitor.getPlatform();
      const info = await _getAppInfo();
      const ref = doc(db, 'USER', uid, 'PUSH_TOKENS', token);
      const snap = await getDoc(ref);
      await setDoc(ref, {
        token,
        platform,
        app_version: info.version,
        app_build: info.build,
        status: 'active',            // re-registro real ressuscita token invalidado
        last_seen_at: serverTimestamp(),
        ...(snap.exists() ? {} : { created_at: serverTimestamp() }),
      }, { merge: true });

      // Espelho legado (senders deployados leem USER.fcm_token) + flag barata
      // de alcance para as métricas (count() em USER.push_reachable).
      await setDoc(doc(db, 'USER', uid), {
        fcm_token: token,
        fcm_token_platform: platform,
        fcm_token_updated: serverTimestamp(),
        push_reachable: true,
      }, { merge: true });
    } catch (e) {
      console.error('[FCM] Falha ao salvar token:', e);
      await this._recordError('save_token', e);
    }
  },

  async _recordPermission(status, trigger) {
    const state = { status, trigger: trigger || null, at: Date.now() };
    _setLocalPerm(state);
    if (!_uid) return;
    try {
      await setDoc(doc(db, 'USER', _uid), {
        push_permission: {
          status,
          trigger: trigger || null,
          platform: Capacitor.getPlatform(),
          updated_at: serverTimestamp(),
        },
        ...(status === 'denied' ? { push_reachable: false } : {}),
      }, { merge: true });
    } catch (e) {
      console.error('[FCM] Falha ao gravar estado de permissão:', e);
    }
  },

  async _recordError(stage, err) {
    if (!_uid) return;
    try {
      await setDoc(doc(db, 'USER', _uid), {
        push_last_error: {
          stage,
          message: String(err?.message || err || 'desconhecido').slice(0, 300),
          platform: Capacitor.getPlatform(),
          at: serverTimestamp(),
        },
      }, { merge: true });
    } catch { /* o erro original já está no console */ }
  },

  // ── Handler de tap na notificação (navegação) ────────────────────────────
  handleNotificationTap(data) {
    const { type } = data;
    console.info('[FCM] Notificação tocada, type:', type);

    switch (type) {

      // ── Chat do VITA no app ──────────────────────────────────────────────
      // Deep link: abre o chat direto, no mesmo padrão desacoplado do
      // QRScanner (CustomEvent no window; este serviço não importa React).
      // VitaChatHost escuta 'lesvita:abrir-vita-chat'.
      case 'vita_chat':
        window.dispatchEvent(new CustomEvent('lesvita:abrir-vita-chat', {
          detail: { naoLidas: Number(data?.nao_lidas || 0) },
        }));
        break;

      // ── Proativo do VITA (briefing, lembrete, chama) — abre o chat ───────
      case 'vita_proativo':
        window.dispatchEvent(new CustomEvent('lesvita:abrir-vita-chat', {
          detail: { naoLidas: Number(data?.nao_lidas || 0) },
        }));
        break;

      // ── Registro feito pelo VITA no WhatsApp (treino/refeição/análise) ──
      // data.route é uma rota contextual do app (/app/treino?id=..., etc.) —
      // mesma malha do DeepLinkRoute. Navegação por location: FCMService não
      // importa React/router (padrão do arquivo).
      case 'vita_registro': {
        const route = String(data?.route || '');
        if (route.startsWith('/')) window.location.assign(route);
        break;
      }

      // ── Ranking / competição ─────────────────────────────────────────────
      case 'ranking_overtaken':
      case 'ranking_ending':
      case 'ranking_week_ending':
        // navigate para aba de ranking/vita
        break;

      // ── Score VITA ───────────────────────────────────────────────────────
      case 'score_dropped':
      case 'score_area_inactive':
        // navigate para aba VITA
        break;

      // ── Premium expirando — abre câmera para escanear QR de renovação ───
      case 'premium_warning_7d':
      case 'premium_warning_1d':
        // Dispara CustomEvent no window.
        // PlataformaPessoal.jsx escuta esse evento e abre o QRScanner.
        // Padrão desacoplado: FCMService não importa React nem componentes.
        window.dispatchEvent(new CustomEvent('lesvita:open-qr-scanner'));
        break;

      // ── Programa Criadores Parceiros ─────────────────────────────────────
      // Abre a plataforma do criador (SSO) na aba do aviso. O host
      // (CriadorHost, montado na plataforma) escuta o evento; em cold start o
      // pedido fica guardado e o host o consome ao montar.
      case 'criador': {
        const aba = String(data?.aba || '') ||
          new URLSearchParams(String(data?.route || '').split('?')[1] || '').get('id') || 'inicio';
        try { sessionStorage.setItem('LV_ABRIR_PLATAFORMA_CRIADOR', aba); } catch { /* sem storage */ }
        window.dispatchEvent(new CustomEvent('lesvita:abrir-plataforma-criador', { detail: { aba } }));
        break;
      }

      // ── Último dia do teste de 7 dias do código de criador ───────────────
      // Só a tela de planos DO APP (regra das lojas: nada de site/WhatsApp).
      case 'premium_teste_ultimo_dia':
        try { sessionStorage.setItem('LV_ABRIR_PLANOS', '1'); } catch { /* sem storage */ }
        window.dispatchEvent(new CustomEvent('lesvita:abrir-planos'));
        break;

      default:
        break;
    }
  },

  // ── Remove listeners (cleanup) ───────────────────────────────────────────
  async cleanup() {
    if (!Capacitor.isNativePlatform()) return;
    if (_valueMomentHandler) {
      window.removeEventListener(VALUE_MOMENT_EVENT, _valueMomentHandler);
      _valueMomentHandler = null;
    }
    _listenersAttached = false;
    _uid = null;
    await PushNotifications.removeAllListeners();
  },
};
